import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import FoodPicker from './FoodPicker'
import { PlanCard, PlanMacros } from '../meal-prep/PlanCards'
import { renderInline } from '../meal-prep/richInline'
import PlanItemPicker from './PlanItemPicker'

const SLOTS = ['breakfast', 'lunch', 'afternoon', 'dinner', 'dessert']
const gramsOf = (ingredient, mapping) => {
  if (mapping && !mapping.skip && mapping.grams) return mapping.grams
  if (ingredient.grams) return ingredient.grams
  const match = String(ingredient.amount || '').match(/^\s*(\d+(?:[.,]\d+)?)\s*(g|ml)\s*$/i)
  return match ? Number(match[1].replace(',', '.')) : ''
}

function IngredientRow({ slot, index, ingredient, mapping, disabled, onEdit }) {
  const { t } = useTranslation()
  const initial = gramsOf(ingredient, mapping)
  const [grams, setGrams] = useState(initial)
  const [swapping, setSwapping] = useState(false)
  const commit = () => {
    const value = Number(grams)
    if (value > 0 && value <= 1500 && value !== Number(initial)) onEdit([{ slot, index, grams: value }])
    else setGrams(initial)
  }
  const skipped = mapping?.skip
  return (
    <li className="py-1 border-b border-dotted border-[#D8DCD5] last:border-b-0" data-testid={`preview-ingredient-${slot}-${index}`}>
      <div className={`text-[14px] ${ingredient.edited ? 'font-medium text-[#0E6B60]' : ''}`}>{renderInline(ingredient.name)}</div>
      <div className="flex flex-wrap items-center gap-2 text-[14px] mt-0.5">
        {skipped ? (
          <span className="font-mono text-[12px] text-[#8B959C]">{ingredient.amount || '—'}</span>
        ) : (
          <input type="number" min="1" max="1500" disabled={disabled} className="border border-[#D8DCD5] rounded px-1.5 py-0.5 w-20 text-sm font-mono bg-white text-[#14181A]"
            aria-label={t('weekPlanning.preview.gramsFor', { name: ingredient.name })} value={grams}
            onChange={(e) => setGrams(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit() } }} />
        )}
        {!skipped && <span className="text-xs text-gray-500">g</span>}
        <button type="button" className="text-xs underline text-[#0E6B60]" disabled={disabled} onClick={() => setSwapping((v) => !v)}>
          {t('weekPlanning.preview.swap')}
        </button>
      </div>
      {swapping && (
        <div className="mt-1">
          <FoodPicker withQuantity initialQuantity={Number(initial) || 100}
            initialQuery={String(ingredient.name || '').replace(/\*/g, '').split(/[,(]/)[0].trim()}
            onCancel={() => setSwapping(false)}
            onPick={({ food_id, quantity_g }) => { setSwapping(false); onEdit([{ slot, index, food_id, grams: quantity_g }]) }} />
        </div>
      )}
    </li>
  )
}

// VH-229 refinement: the menu cards of a plan under review, editable — change grams or swap a food;
// the server recomputes every number, the cards show what comes back.
export function PreviewMenuEditor({ document: doc, disabled = false, onEdit, cycleId = null, planId = null }) {
  const { t, i18n } = useTranslation()
  const [addingTo, setAddingTo] = useState(null)
  const mappings = doc?.food_mappings ?? {}
  const meals = (doc?.daily_menu?.meals ?? []).slice().sort((a, b) => SLOTS.indexOf(a.slot) - SLOTS.indexOf(b.slot))
  const training = doc?.training_day ?? {}
  const card = (slot, label, ingredients, totals, accent = false) => (
    <PlanCard key={slot} label={label} accent={accent} testId={`preview-meal-${slot}`}>
      <ul className="flex flex-col">
        {ingredients.map((ing, i) => (
          <IngredientRow key={`${i}-${ing.candidate_id ?? ''}-${ing.grams ?? ''}`} slot={slot} index={i} ingredient={ing}
            mapping={mappings[`${slot}:${i}`]} disabled={disabled} onEdit={onEdit} />
        ))}
      </ul>
      {addingTo === slot ? (
        <PlanItemPicker cycleId={cycleId} planId={planId} language={i18n.language}
          onCancel={() => setAddingTo(null)}
          onPick={({ food_id, recipe_version_id, grams }) => {
            setAddingTo(null)
            onEdit([{ slot, index: 0, add: true, food_id, recipe_version_id, grams }])
          }} />
      ) : (
        <button type="button" className="mt-1 text-xs underline text-[#0E6B60] self-start" disabled={disabled}
          data-testid={`preview-add-${slot}`} onClick={() => setAddingTo(slot)}>
          {t('weekPlanning.preview.addIngredient')}
        </button>
      )}
      <PlanMacros totals={totals} lang={i18n.language} signed={slot === 'training'} />
    </PlanCard>
  )
  return (
    <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="preview-menu-editor">
      {meals.map((meal) => card(meal.slot, `${t(`weeklyPlan.slot.${meal.slot}`, meal.slot)}${meal.note ? ` · ${meal.note}` : ''}`,
        meal.ingredients ?? [], meal.totals))}
      {(training.ingredients ?? []).length > 0 && card('training', t('weeklyPlan.menu.trainingOnly'), training.ingredients, training.totals, true)}
    </div>
  )
}

export default PreviewMenuEditor
